import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import MultiSelectBox from '../../Common/MultiSelectBox/MultiSelectBox';
import Toggle from '../../Common/Toggle/Toggle';
import Button from '../../Common/Button';
import { CsvToIntArray } from '../../../helpers/strings';
import { getMilestoneIds } from '../../../helpers/reports';
import '../Reports.scss';

export class PastDueTaskListFilters extends React.Component {

    constructor(props, context) {
        super(props, context);

        const initialFormSelections = {
            groupingId: 0,
            locationKeys: '',
            statusIds: '',
            departmentIds: '',
            milestoneIds: '',
            projectIds: '',
            responsibleUserIds: '',
            phaseIds: '',
            communityIds: '',
            disciplineIds: '',
            taskTagIds:'',
            isIncludeCommentsFile: false
        }

        this.state = {
            emptyForm: initialFormSelections,
            showMoreFields: false,
            groupingId: initialFormSelections.groupingId,
            locationKeys: initialFormSelections.locationKeys,
            statusIds: initialFormSelections.statusIds,
            departmentIds: initialFormSelections.departmentIds,
            milestoneIds: initialFormSelections.milestoneIds,
            projectIds: initialFormSelections.projectIds,
            responsibleUserIds: initialFormSelections.responsibleUserIds,
            phaseIds: initialFormSelections.phaseIds,
            communityIds: initialFormSelections.communityIds,
            disciplineIds: initialFormSelections.disciplineIds,
            taskTagIds: initialFormSelections.taskTagIds,
            isIncludeCommentsFile: initialFormSelections.isIncludeCommentsFile
        }
    }

    componentDidMount = async () => {
        /**
         * Must set defaults before registering report filter functions, because
         * these defaults might be overridden by mapToForm values coming from
         * the API when opening a scheduled report.
         */
        this.setDefaults();
        /**
         * On mount, register all of this form's callbacks so the parent is able
         * to use them when its wrapping UI indicates these actions should be
         * performed.
         */
        this.props.registerReportFilterFunctions(
            this.props.report.id,
            this.clearForm,
            this.validateForm,
            this.mapToForm,
            this.mapToApiRequest
        );
    }

    setDefaults = () => {
        let newState = { ...this.state };

        const appState = this.props.savedFilterValues;
        if (appState) {
            // Set new state to values from redux app state
            newState = {
                ...newState,
                groupingId: appState.groupingId,
                locationKeys: appState.locationKeys ? appState.locationKeys : newState.locationKeys,
                statusIds: appState.statusIds ? appState.statusIds : newState.statusIds,
                departmentIds: appState.departmentIds ? appState.departmentIds : newState.departmentIds,
                milestoneIds: appState.milestoneIds ? appState.milestoneIds : newState.milestoneIds,
                projectIds: appState.projectIds ? appState.projectIds : newState.projectIds,
                responsibleUserIds: appState.responsibleUserIds ? appState.responsibleUserIds : newState.responsibleUserIds,
                phaseIds: appState.phaseIds ? appState.phaseIds : newState.phaseIds,
                communityIds: appState.communityIds ? appState.communityIds : newState.communityIds,
                disciplineIds: appState.disciplineIds ? appState.disciplineIds : newState.disciplineIds,
                taskTagIds: appState.taskTagIds ? appState.taskTagIds : newState.taskTagIds,
                isIncludeCommentsFile: appState.isIncludeCommentsFile
            }            
        }


        // Possibly override app state values with default values or URL params
        let groupingId = this.getDefaultGroupingId(newState.groupingId);
        let departmentIds = this.getDefaultDepartmentIds(newState.departmentIds);
        let locationKeys = this.getDefaultLocationKeys(newState.locationKeys);
        let statusIds = this.getDefaultStatusIds(newState.statusIds);
        let milestoneIds = this.getDefaultMilestone(newState.milestoneIds);
        let taskTagIds = this.getDefaultMilestone(newState.taskTagIds);

        newState = {
            ...newState,
            groupingId: groupingId,
            departmentIds: departmentIds,
            locationKeys: locationKeys,
            statusIds: statusIds,
            milestoneIds: milestoneIds,
            taskTagIds: taskTagIds
        }

        this.setState({
            ...this.state,
            ...newState
        });

        if (locationKeys) {
            this.props.onLocationChange(Array.isArray(locationKeys) ? locationKeys : locationKeys.split(","));
        }
    }

    isProjectEnabled = () => {
        this.props.filters && this.props.filters.isProjectEnabled === true;
    }

    getDefaultGroupingId = (savedGroupingId) => {
        /**
         * If the appState already has a saved grouping Id value, then we do not
         * override it with default values under any circumstance.  We only set default
         * grouping Id values if no value has been set yet.
         */
        if (savedGroupingId) {
            return savedGroupingId;
        }

        const PROJECT_GROUP_NAME = "Project";
        const DEPT_GROUP_NAME = "Department";
        /**
         * If Project is active on this subscription, then Project is the default grouping
         * If Project is not active on this subscription, then Department is the default
         * grouping, and the Project option should not be in the drop-down.
         */
        let groupingId = 0;
        if (this.isProjectEnabled()) {
            const grouping = this.props.filters.groupings.find(x => x.name === PROJECT_GROUP_NAME);
            if (grouping && grouping.trim().length > 0) {
                groupingId = grouping.id;
            }
        } else {
            const grouping = this.props.filters.groupings.find(x => x.name === DEPT_GROUP_NAME);
            if (grouping) {
                groupingId = grouping.id;
            }
        }
        return groupingId;
    }

    getDefaultDepartmentIds = (savedDepartmentIds) => {
        /**
         * Departments can come from three places:
         *  1. The URL params
         *  2. Already selected department Ids saved in app state
         *  3. The list of following departments from the API filters.
         *
         * When this report is called from the Task List page of the site, there is an attempt
         * to pass along any departments the user has already selected on that page, to auto-populate
         * the field here.  We try to grab those values first.
         *
         * If the URL params do not contain department values, then we fallback to the saved selected
         * department IDs, and if there are none of those, then we fallback to the following
         * deparments list from the API filters.
         * 
         * If there are no following departments, then we don't set any defaults.
         */
        let defaultDepartmentIds = '';
        const urlDepts = this.props.urlParams.departmentIds;
        const followingDepts = this.props.filters.followingDepts;

        if (urlDepts && urlDepts.length > 0) {
            defaultDepartmentIds = urlDepts.join(',');
        } else if (savedDepartmentIds) {
            defaultDepartmentIds = savedDepartmentIds;
        } else if (followingDepts && followingDepts.length > 0) {
            defaultDepartmentIds = followingDepts.map(x => x.id).join(',');
        }

        return defaultDepartmentIds;
    }

    getDefaultLocationKeys = (savedLocationKeys) => {
        /**
         * Location(s) can come from three places:
         *  1. The URL params
         *  2. Already selected Location Keys saved in app state
         *  3. The current user's default location
         *
         * When this report is called from the Task List page of the site, there is an attempt
         * to pass along any location(s) the user has already selected on that page, to auto-populate
         * the location field here.  We try to grab those values first.
         *
         * If the URL params location(s) aren't set, then we fallback to the selected location keys,
         * and if none have been selected, then we try to pull the default location from the current
         * user's default location.
         */
        let locationKeys = '';
        const urlLocationKeys = this.props.urlParams.locationKeys;
        const defaultLocationKey = this.props.filters.defaultLocationKey;
        if (urlLocationKeys && urlLocationKeys.length > 0) {
            locationKeys = urlLocationKeys.join(',');
        } else if (savedLocationKeys) {
            locationKeys = savedLocationKeys;
        } else if (defaultLocationKey && defaultLocationKey.trim().length > 0) {
            locationKeys = defaultLocationKey;
        }

        return locationKeys;
    }

    getDefaultStatusIds = (savedStatusIds) => {
        /**
         * Default selected values for the status filter only come from optional
         * URL params, so we only check there.  If there are none, we fallback to
         * any previously-selected status id values
         */
        let statusIds = '';
        const urlStatuses = this.props.urlParams.statusIds;
        if (urlStatuses && urlStatuses.length > 0) {
            statusIds = urlStatuses.join(',');
        } else if (savedStatusIds) {
            statusIds = savedStatusIds;
        }

        return statusIds;
    }

    getDefaultMilestone = (savedMilestoneIds) => {
        /**
         * The isMilestone URL param optionally sets one of the values in the
         * hard-coded milestone options list.  0 = Non-milestones, 1 = Milestones.
         * If no URL params have been set, we fallback to any saved milestone Ids
         * that were previously selected.
         */
        let milestoneIds = '';
        const urlIsMilestone = this.props.urlParams.isMilestone;
        if (urlIsMilestone && urlIsMilestone.trim().length > 0) {
            milestoneIds = urlIsMilestone;
        } else if (savedMilestoneIds) {
            milestoneIds = savedMilestoneIds;
        }

        return milestoneIds;
    }

    getDefaultTaskTagIds = (savedTaskTagIds) => {
        /**
         * Default selected values for the taskTagIds filter only come from optional
         * URL params, so we only check there.  If there are none, we fallback to
         * any previously-selected  id values
         */

        let taskTagIds = '';
        const urlStatuses = this.props.urlParams.taskTagIds;
        if (urlStatuses && urlStatuses.length > 0) {
            taskTagIds = urlStatuses.join(',');
        } else if (savedTaskTagIds) {
            taskTagIds = savedTaskTagIds;
        }
        return taskTagIds;
    }


    clearForm = () => {
        this.setState({
            ...this.state,
            ...this.state.emptyForm
        });
    }

    validateForm = () => {
        return true;
    }

    mapToForm = (reportRequest) => {
        const form = { ...this.state.emptyForm };
        const getGroupingId = (grouping) => {
            const groupingOption = this.props.filters.groupings.find(x => x.name === grouping);
            if (groupingOption && groupingOption.id) {
                return groupingOption.id;
            } else {
                return '';
            }
        }

        form.groupingId = (reportRequest.grouping) ? getGroupingId(reportRequest.grouping) : form.groupingId;
        form.locationKeys = (reportRequest.locationKeys) ? reportRequest.locationKeys.join(',') : form.locationKeys;
        form.statusIds = (reportRequest.statusIds) ? reportRequest.statusIds.join(',') : form.statusIds;
        form.departmentIds = (reportRequest.departmentIds) ? reportRequest.departmentIds.join(',') : form.departmentIds;
        form.milestoneIds = getMilestoneIds(reportRequest.isMilestone);
        form.projectIds = (reportRequest.projectIds) ? reportRequest.projectIds.join(',') : form.projectIds;
        form.responsibleUserIds = (reportRequest.responsibleUserIds) ? reportRequest.responsibleUserIds.join(',') : form.responsibleUserIds;
        form.phaseIds = (reportRequest.phaseIds) ? reportRequest.phaseIds.join(',') : form.phaseIds;
        form.communityIds = (reportRequest.communityIds) ? reportRequest.communityIds.join(',') : form.communityIds;
        form.disciplineIds = (reportRequest.disciplineIds) ? reportRequest.disciplineIds.join(',') : form.disciplineIds;
        form.taskTagIds = (reportRequest.taskTagIds) ? reportRequest.taskTagIds.join(',') : form.taskTagIds;
        form.isIncludeCommentsFile = (reportRequest.shouldIncludeCommentFiles === true) ? true : false;

        this.setState({
            ...this.state,
            ...form
        });

        /** 
         * Make sure to trigger the parent's loading of the proper responsible user
         * values based on the newly loaded location keys
         */
        if (reportRequest.locationKeys) {
            this.props.onLocationChange(reportRequest.locationKeys);
        }
    }

    /**
     * When an API request is going to be made, we also want to save the state
     * of our form entries into redux, so that saved state can be loaded when
     * this report is scheduled or simply reloaded later.  To accomplish that
     * from the centralized ReportDataHelper, we return both the apiRequest
     * object as well as the current state of our form.  This separation allows
     * for differences in the apiRequest format/naming conventions and those of
     * the form fields.
     */
    mapToApiRequest = () => {
        let isMilestone = null; // default to none or both
        if (this.state.milestoneIds.length > 0) {
            const selectedMilestoneIds = this.state.milestoneIds.split(',');
            if (selectedMilestoneIds.length == 1) {
                isMilestone = selectedMilestoneIds[0] === '1' ? true : false;
            }
        }

        let groupingName = null;
        const grouping = this.props.filters.groupings.find(x => x.id === this.state.groupingId);
        if (grouping) {
            groupingName = grouping.name;
        }

        const request = {
            reportId: this.props.report.id,
            grouping: groupingName,
            locationKeys: this.state.locationKeys.split(','),
            statusIds: CsvToIntArray(this.state.statusIds),
            departmentIds: CsvToIntArray(this.state.departmentIds),
            isMilestone: isMilestone,
            projectIds: CsvToIntArray(this.state.projectIds),
            responsibleUserIds: CsvToIntArray(this.state.responsibleUserIds),
            phaseIds: CsvToIntArray(this.state.phaseIds),
            communityIds: CsvToIntArray(this.state.communityIds),
            disciplineIds: CsvToIntArray(this.state.disciplineIds),
            taskTagIds: CsvToIntArray(this.state.taskTagIds),
            shouldIncludeCommentFiles: this.state.isIncludeCommentsFile
        };

        return {
            apiRequest: request,
            formValues: this.state
        };
    }

    onFieldChange = (name, value) => {
        this.setState({
            ...this.state,
            [name]: value
        });
    }

    onDropdownChange = (value, name) => {
        this.onFieldChange(name, value);
    }

    onDatePickerChange = (date, name) => {
        this.onFieldChange(name, date);
    }

    onToggleShowMore = () => {
        this.setState({
            showMoreFields: !this.state.showMoreFields
        });
    }

    onLocationChange = (value, name) => {
        // Save the changed value in our local state, as usual
        this.onFieldChange(name, value);

        // Tell our parent the location value has changed so they can reload the
        // potential values for the Responsible User field
        let locationKeys = [];
        if (value && value.trim().length > 0) {
            locationKeys = value.split(',');
            this.props.onLocationChange(locationKeys);
        }
    }

    render() {
        const showMoreText = this.state.showMoreFields ? "Show Less Filters" : "Show More Filters";

        return (
            <form id="subForm" noValidate className={this.props.isFormValid ? "" : "was-validated"}>
                <div className="row">
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="groupingId"
                            label="Group By" idField="id" valueField="name"
                            options={this.props.filters.groupings}
                            selectedValues={this.state.groupingId.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={false} />
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="locationKeys"
                            label="Location" idField="id" valueField="name"
                            options={this.props.filters.locations}
                            selectedValues={this.state.locationKeys.toString()}
                            onChange={this.onLocationChange}
                            isRequired={false}
                            errorMessage="Location is required"
                            isMulti={true} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="statusIds"
                            label="Status" idField="id" valueField="name"
                            options={this.props.filters.statuses}
                            selectedValues={this.props.filters.statuses.map(s => s.id).toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} 
                            isDisabled={true} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="departmentIds"
                            label="Department" idField="id" valueField="name"
                            options={this.props.filters.departments}
                            selectedValues={this.state.departmentIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="disciplineIds"
                            label="Discipline" idField="id" valueField="name"
                            options={this.props.filters.disciplines}
                            selectedValues={this.state.disciplineIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="milestoneIds"
                            label="Milestone" idField="id" valueField="name"
                            options={this.props.filters.milestones}
                            selectedValues={this.state.milestoneIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                    {
                        this.props.filters.isProjectEnabled && this.props.filters.projects &&
                        <div className="col-sm-12 col-md-4">
                            <MultiSelectBox name="projectIds"
                                label="Project" idField="id" valueField="name"
                                options={this.props.filters.projects}
                                selectedValues={this.state.projectIds.toString()}
                                onChange={this.onDropdownChange}
                                isRequired={false}
                                isMulti={true} />
                        </div>
                    }
                </div>
                {
                    this.state.showMoreFields &&
                    <Fragment>
                        <div className="row">
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="responsibleUserIds"
                                    label="Responsible User" idField="id" valueField="name"
                                    options={this.props.filters.responsibleUsers}
                                    selectedValues={this.state.responsibleUserIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="phaseIds"
                                    label="Phase" idField="id" valueField="name"
                                    options={this.props.filters.phases}
                                    selectedValues={this.state.phaseIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="communityIds"
                                    label="Community" idField="id" valueField="name"
                                    options={this.props.filters.communities}
                                    selectedValues={this.state.communityIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="taskTagIds"
                                    label="Task Tag" idField="id" valueField="name"
                                    options={this.props.filters.taskTags}
                                    selectedValues={this.state.taskTagIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-sm-12 col-md-4">
                                <Toggle name="isIncludeCommentsFile"
                                    text="Include Comments/Files"
                                    checked={this.state.isIncludeCommentsFile}
                                    onChange={this.onFieldChange} />
                            </div>
                        </div>
                    </Fragment>
                }
                <div className="row text-end">
                    <div className="col-sm-12">
                        <Button name="showMore" type="button" bootstrapClass="link" text={showMoreText} onClick={this.onToggleShowMore} />
                    </div>
                </div>
            </form>
        );
    }
}

PastDueTaskListFilters.propTypes = {
    report: PropTypes.object.isRequired,
    filters: PropTypes.object.isRequired,
    urlParams: PropTypes.object.isRequired,
    savedFilterValues: PropTypes.object.isRequired,
    isFormValid: PropTypes.bool.isRequired,
    registerReportFilterFunctions: PropTypes.func.isRequired,
    onLocationChange: PropTypes.func.isRequired
};

export default PastDueTaskListFilters;
